import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useStatsigClient } from '@statsig/react-bindings';
import PropTypes from 'prop-types';

import {
  updateNarrativeName,
  shareNarrativeWithClientGroup,
  shareNarrativeWithUsers,
  readNarrativeType,
} from '@api/narratives';
import { GATES } from '@constants';
import { narrativeStatus, flowTopicTag, formatDate, setEndpoint } from '@helpers';
import {
  DownloadIcon,
  EditableTitle,
  HelpPlainIcon,
  OverlayTriggerTooltip,
  RepromptIcon,
  ShareIcon,
  toast,
  Toolbar,
} from '@utilities';

import ModalShare from '../../../ModalShare';
import Download from '../../../Builder/components/Toolbar/components/Download';
import SaveAsTemplate from '../../../SaveAsTemplate';
import TitleFooter from './components/TitleFooter';
import { filterPrompts } from '../../../../pages/Narratives/components/ModalBuildNarrative/utilities/helpers';
import { getHelpArticleURLByNarrativeId } from '../../utilities/helpers';

import styles from './_index.module.scss';

const NarrativeTitle = ({
  downloadData,
  flowData,
  handleReprompt,
  isDraft = false,
  publicToken,
  selectedPromptsOverride = null,
  setFlow,
  titleOverride = null,
  topicIds = null,
}) => {
  const [name, setName] = useState(null);
  const [newName, setNewName] = useState(null);
  const [showModalShare, setShowModalShare] = useState(false);
  const [selectedPrompts, setSelectedPrompts] = useState([]);
  const [isDownloadDropdownActive, setIsDownloadDropdownActive] = useState(false);

  const { permissions = {} } = useSelector((state) => state?.user?.data) || {};
  const { client } = useStatsigClient();
  const { canAccessChat, canShareReportsPublicly } = permissions;

  const allowPublicShare =
    canShareReportsPublicly && client.checkGate(GATES.NARRATIVE_PUBLIC_SHARE);
  const isPublicShare = Boolean(publicToken);

  const downloadRef = useRef(null);

  const downloadFileUrl = setEndpoint(`api/flows/${flowData?.id}/export`);

  const hasPreviewTag = (data) => data?.tags && flowTopicTag.PREVIEW in data.tags;

  const downloadableData = useMemo(() => {
    if (!flowData || !downloadData || downloadData.size === 0) return null;

    // Sort data by topic order from flowData and filter out preview topics
    const orderedData = flowData.topics
      .filter((topic) => !hasPreviewTag(topic))
      .map((topic) => downloadData.get(topic.id))
      .filter(Boolean);

    return {
      data: orderedData,
      exportName: flowData.name || 'Narrative',
      // ModelBuilder assign date range to an object instead of an array when reprompt is selected
      reportDateRange: flowData.answers?.date_range?.date_range ||
        flowData?.answers?.date_range || [null, null],
      reportName: flowData.name || 'Narrative',
      reportType: `${flowData.narrativeName} Narrative`,
      reportUrl: `${window.location.origin}/dashboard/narrative/${flowData.id}`,
    };
  }, [
    downloadData,
    flowData?.topics,
    flowData?.name,
    flowData?.answers?.date_range,
    flowData?.narrativeName,
    flowData?.id,
  ]);

  const customFileName = useMemo(() => {
    const narrativeType = flowData?.narrativeName || 'Narrative';
    const narrativeTitle = flowData?.name || 'Narrative';
    const narrativeDate = flowData?.createdDatetime
      ? new Date(flowData?.createdDatetime).toISOString()
      : '';

    const safeNarrativeType = narrativeType.replace(/\s+/g, '_');
    const safeNarrativeTitle = narrativeTitle.replace(/\s+/g, '_');
    const formattedNarrativeDate = narrativeDate ? formatDate(narrativeDate, 'YYYY-MM-DD') : '';

    let formattedFileName = `${safeNarrativeType}_${safeNarrativeTitle}_${formattedNarrativeDate}`;
    formattedFileName = formattedFileName.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    formattedFileName = formattedFileName.replace(/[^a-zA-Z0-9_-]/g, '');

    return formattedFileName;
  }, [flowData?.narrativeName, flowData?.name, flowData?.createdDatetime]);

  const isDownloadAvailable = flowData?.status === narrativeStatus.COMPLETED && downloadableData;
  const downloadTooltip = isDownloadAvailable
    ? 'Download'
    : 'Download will be available after report has finished processing';

  const shareTooltip =
    flowData?.status === narrativeStatus.COMPLETED
      ? 'Share'
      : 'Share will be available after report has finished processing';

  const normalizedExternalTopicIds = useMemo(() => {
    if (!Array.isArray(topicIds)) return [];
    return topicIds.filter(
      (topicId) => topicId != null && (typeof topicId !== 'string' || topicId.trim().length > 0)
    );
  }, [topicIds]);
  const normalizedFlowTopicIds = useMemo(() => {
    if (!Array.isArray(flowData?.topic_ids)) return [];
    return flowData.topic_ids.filter(
      (topicId) => topicId != null && (typeof topicId !== 'string' || topicId.trim().length > 0)
    );
  }, [flowData?.topic_ids]);
  const normalizedFlowDerivedTopicIds = useMemo(() => {
    if (!Array.isArray(flowData?.topics)) return [];
    return flowData.topics
      .filter((topic) => !topic?.isHidden)
      .map((topic) => topic?.topicId)
      .filter(
        (topicId) => topicId != null && (typeof topicId !== 'string' || topicId.trim().length > 0)
      );
  }, [flowData?.topics]);
  const saveTemplateTopicIds = useMemo(() => {
    if (normalizedExternalTopicIds.length > 0) return normalizedExternalTopicIds;
    if (normalizedFlowTopicIds.length > 0) return normalizedFlowTopicIds;
    return normalizedFlowDerivedTopicIds;
  }, [normalizedExternalTopicIds, normalizedFlowDerivedTopicIds, normalizedFlowTopicIds]);
  const dynamicTopicPrompts = useMemo(() => {
    if (flowData?.narrative_id !== 'dynamic') return null;
    if (normalizedExternalTopicIds.length > 0) return normalizedExternalTopicIds;
    if (normalizedFlowTopicIds.length > 0) return normalizedFlowTopicIds;
    return null;
  }, [flowData?.narrative_id, normalizedExternalTopicIds, normalizedFlowTopicIds]);
  const dynamicTopicPromptsSignature = useMemo(
    () => JSON.stringify(dynamicTopicPrompts || []),
    [dynamicTopicPrompts]
  );
  const parsedDynamicTopicPrompts = useMemo(
    () => (dynamicTopicPromptsSignature === '[]' ? null : JSON.parse(dynamicTopicPromptsSignature)),
    [dynamicTopicPromptsSignature]
  );

  useEffect(() => {
    const isDynamic = flowData?.narrative_id === 'dynamic';

    if ((isDraft && !isDynamic) || !flowData?.id || !flowData?.narrative_id) return;

    const initializeSelectedPrompts = async () => {
      try {
        const hasDynamicTopicPrompts = Array.isArray(parsedDynamicTopicPrompts);

        const data = await readNarrativeType({
          flowId: hasDynamicTopicPrompts ? null : flowData.id,
          narrativeId: flowData?.narrative_id,
          publicToken: publicToken,
          topicIds: hasDynamicTopicPrompts ? parsedDynamicTopicPrompts : null,
        });

        const prompts = data?.prompts || [];
        setSelectedPrompts(filterPrompts(prompts));
      } catch (error) {
        console.error('Error fetching Narrative prompts:', error);
        toast('Failed to load prompts.', { status: 'error' });
      }
    };

    initializeSelectedPrompts();
  }, [flowData?.id, flowData?.narrative_id, isDraft, parsedDynamicTopicPrompts, publicToken]);

  useEffect(() => {
    if (!newName) return;

    if (isDraft) {
      setFlow((prev) => ({ ...prev, name: newName }));
      return;
    }

    if (!flowData?.id) return;

    const updateFlowName = async () => {
      try {
        await updateNarrativeName({ id: flowData.id, name: newName });
        setFlow((prev) => ({ ...prev, name: newName }));
      } catch (error) {
        console.error(error);
      }
    };

    updateFlowName();
  }, [flowData?.id, isDraft, newName, setFlow]);

  const footerSelectedPrompts =
    Array.isArray(selectedPromptsOverride) && selectedPromptsOverride.length > 0
      ? selectedPromptsOverride
      : selectedPrompts;
  const titleValue = titleOverride ?? flowData?.name;

  return (
    <div className={styles['narrative-title-container']}>
      <div className={styles['narrative-title-toolbar-wrapper']}>
        {!publicToken ? (
          <Toolbar
            className={styles['narrative-title-toolbar']}
            tools={[
              <OverlayTriggerTooltip aria-label="Share" content={shareTooltip} key="share">
                <button
                  className={styles['action-icon']}
                  data-testid="share-button"
                  disabled={flowData?.status !== narrativeStatus.COMPLETED}
                  onClick={() => setShowModalShare(true)}
                >
                  <ShareIcon />
                </button>
              </OverlayTriggerTooltip>,
              <span key="download" ref={downloadRef}>
                <OverlayTriggerTooltip
                  aria-label="Download"
                  content={downloadTooltip}
                  key="download"
                >
                  <>
                    <button
                      className={styles['action-icon']}
                      data-testid="download-dropdown-button"
                      disabled={!isDownloadAvailable}
                      onClick={() => setIsDownloadDropdownActive(true)}
                    >
                      <DownloadIcon />
                    </button>
                    <Download
                      customFileName={customFileName}
                      downloadData={downloadableData}
                      downloadFileUrl={downloadFileUrl}
                      downloadLabel={
                        flowData?.narrativeName || flowData?.narrative_name || 'Narrative'
                      }
                      isAvailable={isDownloadAvailable}
                      isDownloadDropdownActive={isDownloadDropdownActive}
                      setIsDownloadDropdownActive={setIsDownloadDropdownActive}
                      target={downloadRef?.current}
                    />
                  </>
                </OverlayTriggerTooltip>
              </span>,
              <OverlayTriggerTooltip aria-label="Reprompt" content="Reprompt" key="reprompt">
                <button
                  className={styles['action-icon']}
                  data-testid="reprompt-button"
                  disabled={isDraft}
                  onClick={handleReprompt}
                >
                  <RepromptIcon />
                </button>
              </OverlayTriggerTooltip>,
              <SaveAsTemplate
                canAccessChat={canAccessChat}
                isPublicShare={isPublicShare}
                key="save-template"
                topicIds={saveTemplateTopicIds}
                triggerClassName={styles['action-icon']}
                triggerVariant="icon"
              />,
              <OverlayTriggerTooltip aria-label="Help" content="Help" key="help">
                <a
                  className={`${styles['action-icon']} ${styles['help-icon']}`}
                  data-log="Flow_Help_Center"
                  data-log-properties={JSON.stringify({
                    id: flowData?.id,
                    type: flowData?.narrative_id,
                  })}
                  data-testid="help-button"
                  href={getHelpArticleURLByNarrativeId(flowData?.narrative_id)}
                  rel="noreferrer"
                  target="_blank"
                >
                  <HelpPlainIcon />
                </a>
              </OverlayTriggerTooltip>,
            ]}
          >
            {!isDraft ? (
              <EditableTitle
                autoFocus={false}
                className={styles['narrative-title']}
                isDisabled={flowData?.isReadOnly}
                onBlur={setNewName}
                onChange={setName}
                value={name !== null ? name : titleValue}
              />
            ) : (
              <EditableTitle
                autoFocus={false}
                className={styles['narrative-title']}
                isDisabled={false}
                onBlur={setNewName}
                onChange={setName}
                value={name !== null ? name : titleValue}
              />
            )}
          </Toolbar>
        ) : (
          <Toolbar
            className={styles['narrative-title-toolbar']}
            tools={[
              <OverlayTriggerTooltip aria-label="Help" content="Help" key="help">
                <a
                  className={`${styles['action-icon']} ${styles['help-icon']}`}
                  data-log="Flow_Help_Center"
                  data-log-properties={JSON.stringify({
                    id: flowData?.id,
                    type: flowData?.narrative_id,
                  })}
                  data-testid="help-button"
                  href={getHelpArticleURLByNarrativeId(flowData?.narrative_id)}
                  rel="noreferrer"
                  target="_blank"
                >
                  <HelpPlainIcon />
                </a>
              </OverlayTriggerTooltip>,
            ]}
          >
            <EditableTitle
              autoFocus={false}
              className={styles['narrative-title']}
              isDisabled
              onChange={() => {}}
              value={titleValue}
            />
          </Toolbar>
        )}
      </div>
      {!isDraft && showModalShare && (
        <ModalShare
          hasSharePublicly={allowPublicShare}
          ids={[flowData?.id]}
          isNarrative
          isSurvey={false}
          setShowModalShare={setShowModalShare}
          shareItemWithClients={shareNarrativeWithClientGroup}
          shareItemWithUsers={shareNarrativeWithUsers}
        />
      )}
      <TitleFooter
        answers={flowData?.answers || {}}
        createdDatetime={flowData?.createdDatetime}
        handleReprompt={handleReprompt}
        isDraft={isDraft}
        isPublicShare={isPublicShare}
        narrativeFlowStatus={flowData?.status}
        selectedPrompts={footerSelectedPrompts}
      />
    </div>
  );
};

NarrativeTitle.propTypes = {
  downloadData: PropTypes.object,
  flowData: PropTypes.object,
  handleReprompt: PropTypes.func,
  isDraft: PropTypes.bool,
  publicToken: PropTypes.string,
  selectedPromptsOverride: PropTypes.array,
  setFlow: PropTypes.func,
  titleOverride: PropTypes.string,
  topicIds: PropTypes.arrayOf(PropTypes.string),
};

export default NarrativeTitle;
